import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, ScrollText, CheckCircle2, Info, Sparkles, Rocket, ArrowRight, Lock, Award, ShieldCheck, Target, TrendingUp, Brain, Briefcase, Camera, Layers, ChevronRight, Trophy, Star, GraduationCap, UserCircle, Gift, PartyPopper, Heart, Zap, Globe, PlayCircle, Film, Music, Building2, Mail, Tv, Banana, Code, Cpu, MapPin, Wifi, HandHeart, UserPlus, Percent, RefreshCw, DollarSign, Coins, Gem, Wine, Crown, Mic, Monitor, Calendar, Clock, BookOpen, Lightbulb, Compass } from 'lucide-react';
import { Navbar } from '../components/Navbar';
// Footer import removed — landing page uses a minimal inline footer to keep focus on the application
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
// ---------- Primitives ----------
const Eyebrow = ({
  children
}: {
  children: React.ReactNode;
}) => <p className="text-amber-400/70 tracking-[0.3em] text-[10px] sm:text-xs uppercase font-light" data-id="element-3128">
    {children}
  </p>;
const SectionHeading = ({
  eyebrow,
  title,
  highlight,
  intro
}: {
  eyebrow: string;
  title: string;
  highlight?: string;
  intro?: string;
}) => <div className="max-w-3xl mx-auto text-center" data-id="element-3129">
    <Eyebrow data-id="element-3130">{eyebrow}</Eyebrow>
    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-3131">
      {title}
      {highlight && <>
          {' '}
          <span className="text-amber-400" data-id="element-3132">{highlight}</span>
        </>}
    </h2>
    <div className="w-16 h-px bg-amber-400/30 mb-6 mx-auto" data-id="element-3133" />
    {intro && <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-3134">
        {intro}
      </p>}
  </div>;
const PremiumCard = ({
  icon: Icon,
  title,
  body,
  highlight = false
}: {
  icon: React.ElementType;
  title: string;
  body: string;
  highlight?: boolean;
}) => <div className={`group relative border ${highlight ? 'border-amber-400/40' : 'border-amber-400/15'} bg-gradient-to-b from-amber-950/10 to-transparent hover:border-amber-400/50 transition-colors duration-500 p-6 sm:p-7 h-full`} data-id="element-3135">
    <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-3136" />
    <div className="w-10 h-10 border border-amber-400/40 bg-black/40 flex items-center justify-center mb-5" data-id="element-3137">
      <Icon className="w-4 h-4 text-amber-400" data-id="element-3138" />
    </div>
    <h3 className="font-serif text-lg sm:text-xl text-white mb-3 leading-tight" data-id="element-3139">
      {title}
    </h3>
    <p className="text-sm sm:text-[15px] text-white/60 leading-relaxed font-light" data-id="element-3140">
      {body}
    </p>
  </div>;
// ====================================== TRAILER INTRO (above hero)
const TrailerIntro = () => <section id="trailer-intro" className="relative bg-black pt-24 pb-16 md:pt-28 md:pb-20 overflow-hidden" data-id="element-3141">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08),_transparent_70%)] pointer-events-none" data-id="element-3142" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3143">
      <div className="max-w-5xl mx-auto" data-id="element-3144">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7
      }} className="text-center mb-8" data-id="element-3145">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/40 bg-amber-400/10 mb-5" data-id="element-3146">
            <PlayCircle className="w-3.5 h-3.5 text-amber-400" data-id="element-3147" />
            <span className="text-amber-400 tracking-[0.3em] text-[10px] uppercase font-bold" data-id="element-3148">
              The World You&rsquo;d Be Building
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white leading-[1.05] tracking-tight mb-4" data-id="element-3149">
            Canada&rsquo;s Most Ambitious{' '}
            <span className="text-amber-400 italic" data-id="element-3150">
              Immersive Cultural Production
            </span>
          </h2>
          <p className="text-white/55 text-sm sm:text-base font-light max-w-2xl mx-auto" data-id="element-3151">
            Before you apply, see what fellows contribute to — a live,
            large-scale Sikh Kingdoms experience being built across film,
            immersive, AI, and culture.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative border border-amber-400/30 bg-black p-2 shadow-[0_0_60px_rgba(212,175,55,0.2)]" data-id="element-3152">
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3153" />
          <div className="relative aspect-video bg-black overflow-hidden" data-id="element-3154">
            <iframe className="absolute inset-0 w-full h-full" src="https://www.youtube.com/embed/2yMWvJinVgk?rel=0" title="Sikh Kingdoms — Official Experience Trailer" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-3155" />
          </div>
        </motion.div>

        <p className="text-center mt-5 text-white/40 text-xs tracking-wide font-light italic" data-id="element-3156">
          Official Experience Trailer · Sikh Kingdoms
        </p>
      </div>
    </div>
  </section>;
// ====================================== HERO
const Hero = () => <section className="relative w-full bg-black flex items-center justify-center overflow-hidden py-20 md:py-24 border-t border-amber-400/10" data-id="element-3157">
    <div className="absolute inset-0" data-id="element-3158">
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black" data-id="element-3159" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.18),_transparent_55%)] pointer-events-none" data-id="element-3160" />
    </div>
    <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 0.5
  }} transition={{
    duration: 2,
    delay: 0.5
  }} className="absolute top-32 left-[10%] hidden lg:block" data-id="element-3161">
      <Sparkles className="w-6 h-6 text-amber-400/40" data-id="element-3162" />
    </motion.div>
    <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 0.4
  }} transition={{
    duration: 2,
    delay: 1
  }} className="absolute bottom-40 right-[8%] hidden lg:block" data-id="element-3163">
      <Zap className="w-7 h-7 text-amber-400/40" data-id="element-3164" />
    </motion.div>

    <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-3165">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7
    }} className="inline-flex flex-wrap items-center justify-center gap-2 mb-7" data-id="element-3166">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-amber-400 bg-amber-400/10 shadow-[0_0_20px_rgba(212,175,55,0.2)]" data-id="element-3167">
          <GraduationCap className="w-3 h-3 text-amber-400" data-id="element-3168" />
          <span className="text-amber-400 tracking-[0.3em] text-[9px] sm:text-[10px] uppercase font-bold" data-id="element-3169">
            High School &amp; University
          </span>
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-amber-400 bg-amber-400/10 shadow-[0_0_20px_rgba(212,175,55,0.2)]" data-id="element-3170">
          <Calendar className="w-3 h-3 text-amber-400" data-id="element-3171" />
          <span className="text-amber-400 tracking-[0.3em] text-[9px] sm:text-[10px] uppercase font-bold" data-id="element-3172">
            Just 3 Hours / Week
          </span>
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-amber-400 bg-amber-400/10 shadow-[0_0_20px_rgba(212,175,55,0.2)]" data-id="element-3173">
          <Gift className="w-3 h-3 text-amber-400" data-id="element-3174" />
          <span className="text-amber-400 tracking-[0.3em] text-[9px] sm:text-[10px] uppercase font-bold" data-id="element-3175">
            Free for Selected Students
          </span>
        </span>
      </motion.div>

      <motion.h1 initial={{
      opacity: 0,
      y: 24
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.9,
      delay: 0.1
    }} className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight mb-4" data-id="element-3176">
        <span className="block text-amber-400/80 font-serif italic text-base sm:text-lg md:text-xl tracking-[0.3em] uppercase not-italic mb-4 sm:mb-5" data-id="element-3177">
          Summer Fellowship
        </span>
        Entrepreneurship and
        <br className="hidden sm:block" data-id="element-3178" />{' '}
        <span className="text-amber-400" data-id="element-3179">AI Opportunity</span>
      </motion.h1>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.9,
      delay: 0.3
    }} className="font-serif italic text-base sm:text-lg md:text-xl text-amber-400/90 mb-3 max-w-3xl mx-auto" data-id="element-3180">
        Canada&rsquo;s Premier Punjabi Entrepreneurship and AI Discovery
        Fellowship
      </motion.p>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.9,
      delay: 0.4
    }} className="flex flex-col items-center gap-2 mb-7" data-id="element-3181">
        <p className="text-amber-400/90 text-xs tracking-[0.3em] uppercase font-bold" data-id="element-3182">
          Presented by Akal Steel
        </p>
        <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-light" data-id="element-3183">
          In partnership with the Sikh Foundation of Canada
        </p>
      </motion.p>

      <div className="w-24 h-px bg-amber-400/50 mx-auto mb-7" data-id="element-3184" />

      <motion.p initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.5
    }} className="text-base sm:text-lg md:text-xl text-white/70 font-light leading-relaxed max-w-3xl mx-auto mb-5" data-id="element-3185">
        Build real things alongside{' '}
        <span className="text-amber-400 font-medium" data-id="element-3186">
          Emmy &amp; Oscar-winning creators
        </span>
        , learn entrepreneurship from professors at the world&rsquo;s top
        business schools — including{' '}
        <span className="text-amber-400 font-medium" data-id="element-3187">Ivey</span>{' '}
        <span className="text-white/55 text-sm" data-id="element-3188">(Canada&rsquo;s #1)</span> and{' '}
        <span className="text-amber-400 font-medium" data-id="element-3189">INSEAD</span>{' '}
        <span className="text-white/55 text-sm" data-id="element-3190">(Europe&rsquo;s #1)</span> — and
        earn a letter of recommendation that actually matters.
      </motion.p>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.8,
      delay: 0.6
    }} className="text-white/55 text-sm sm:text-base font-light italic max-w-2xl mx-auto mb-4" data-id="element-3191">
        For high school and university students. Apply solo — or with a friend.
      </motion.p>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.8,
      delay: 0.65
    }} className="text-white/40 text-xs sm:text-sm font-light max-w-2xl mx-auto mb-10" data-id="element-3192">
        <span className="text-amber-400/80" data-id="element-3193">Parents:</span> this is a
        structured, mentor-led summer program.{' '}
        <span className="text-white/55" data-id="element-3194">Just 3 hours a week.</span> Designed for
        ambitious students. Free for those selected.
      </motion.p>

      <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.75
    }} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8" data-id="element-3195">
        <a href="#apply" className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-amber-400 text-black text-xs tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_50px_rgba(212,175,55,0.4)]" data-id="element-3196">
          Apply Now
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" data-id="element-3197" />
        </a>
        <a href="#at-a-glance" className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-3198">
          <Info className="w-4 h-4" data-id="element-3199" />
          See the Details
        </a>
      </motion.div>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.7,
      delay: 1
    }} className="text-amber-400 font-bold text-xs tracking-[0.4em] uppercase" data-id="element-3200">
        Earn Your Place
      </motion.p>
    </div>
  </section>;
// ====================================== CNA FEATURED VIDEO
const CNAFeatured = () => <section id="featured" className="relative py-20 md:py-24 bg-black border-t border-amber-400/10 scroll-mt-20" data-id="element-3201">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.07),_transparent_70%)] pointer-events-none" data-id="element-3202" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3203">
      <div className="max-w-4xl mx-auto" data-id="element-3204">
        <div className="text-center mb-10" data-id="element-3205">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/40 bg-amber-400/10 mb-5" data-id="element-3206">
            <Tv className="w-3.5 h-3.5 text-amber-400" data-id="element-3207" />
            <span className="text-amber-400 tracking-[0.3em] text-[10px] uppercase font-bold" data-id="element-3208">
              Featured by Channel News Asia
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mb-3 leading-tight" data-id="element-3209">
            Professor Ash Singh on{' '}
            <span className="text-amber-400 italic" data-id="element-3210">Millionaire Minds</span>
          </h2>
          <p className="text-white/55 text-sm sm:text-base font-light max-w-xl mx-auto" data-id="element-3211">
            Featured globally on CNA&rsquo;s Millionaire Minds — the
            entrepreneurship faculty leading this fellowship.
          </p>
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative border border-amber-400/30 bg-black p-2 shadow-[0_0_60px_rgba(212,175,55,0.2)]" data-id="element-3212">
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3213" />
          <div className="relative aspect-video bg-black overflow-hidden" data-id="element-3214">
            <iframe className="absolute inset-0 w-full h-full" src="https://www.youtube.com/embed/2c0eBgOG2yU?rel=0" title="Professor Ash Singh — Channel News Asia: Millionaire Minds" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-3215" />
          </div>
        </motion.div>
        <p className="text-center mt-5 text-white/40 text-xs tracking-wide font-light italic" data-id="element-3216">
          Channel News Asia · Millionaire Minds
        </p>
      </div>
    </div>
  </section>;
// ====================================== AT A GLANCE (parent-friendly quick facts)
const AtAGlance = () => {
  const facts = [{
    icon: GraduationCap,
    label: 'Who',
    value: 'High school & university students'
  }, {
    icon: Clock,
    label: 'Time',
    value: 'Just 3 hours / week + optional extra work'
  }, {
    icon: Calendar,
    label: 'When',
    value: 'Summer 2026 cohort'
  }, {
    icon: MapPin,
    label: 'Where',
    value: 'Toronto (Distillery District) or Virtual'
  }, {
    icon: DollarSign,
    label: 'Cost',
    value: 'Free · Refundable $250 commitment'
  }, {
    icon: Mail,
    label: 'You Earn',
    value: 'Recommendation from Ivey & INSEAD faculty'
  }];
  return <section id="at-a-glance" className="relative py-20 md:py-24 bg-[#080808] border-t border-amber-400/10 scroll-mt-20" data-id="element-3217">
      <div className="container mx-auto px-4" data-id="element-3218">
        <div className="max-w-3xl mx-auto text-center mb-10" data-id="element-3219">
          <Eyebrow data-id="element-3220">For Students and Parents</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-5 leading-tight" data-id="element-3221">
            The Opportunity, <span className="text-amber-400" data-id="element-3222">At a Glance</span>
          </h2>
          <div className="w-16 h-px bg-amber-400/30 mb-5 mx-auto" data-id="element-3223" />
          <p className="text-white/60 text-sm sm:text-base font-light leading-relaxed" data-id="element-3224">
            Everything parents and students need to know in one scan.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-5xl mx-auto" data-id="element-3225">
          {facts.map((f, i) => <motion.div key={f.label} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.05
        }} className="relative border border-amber-400/20 bg-gradient-to-br from-amber-950/12 to-transparent p-5 flex items-start gap-4" data-id="element-3226">
              <span className="absolute -top-px left-5 right-5 h-px bg-gradient-to-r from-transparent via-amber-400/55 to-transparent" data-id="element-3227" />
              <div className="w-10 h-10 border border-amber-400/40 bg-black/40 flex items-center justify-center shrink-0" data-id="element-3228">
                <f.icon className="w-4 h-4 text-amber-400" data-id="element-3229" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-3230">
                <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold mb-1" data-id="element-3231">
                  {f.label}
                </p>
                <p className="text-white text-sm font-light leading-snug" data-id="element-3232">
                  {f.value}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// ====================================== WHY THIS MATTERS (parents + students)
const WhyThisMatters = () => {
  const parentPoints = [{
    icon: Lightbulb,
    title: 'AI is the defining skill of this decade',
    body: 'Students who learn to build with AI now will lead the next generation of industries — careers, universities, and creative work are all reshaping around it.'
  }, {
    icon: BookOpen,
    title: 'A serious credential for university',
    body: 'A real letter of recommendation from Ivey (Canada\u2019s #1) and INSEAD (Europe\u2019s #1) faculty — earned through actual work, not attendance.'
  }, {
    icon: ShieldCheck,
    title: 'Safe, supervised, and structured',
    body: 'Mentor-led sessions, weekly Friday check-ins, individual MBA pairing. Your child is supported, not left to figure it out alone.'
  }, {
    icon: Clock,
    title: 'Only 3 hours a week',
    body: 'Designed to fit alongside school, summer plans, and family time. Optional extra work for students who want to go deeper.'
  }, {
    icon: DollarSign,
    title: 'Fully sponsored',
    body: 'The program itself is free for selected students. A refundable $250 commitment deposit is returned in full on completion.'
  }, {
    icon: Compass,
    title: 'Real mentorship, not a class',
    body: 'Emmy and Oscar-winning creators, top global business school faculty, MBA students — your child works alongside people who have actually built things.'
  }];
  const studentPoints = [{
    icon: Rocket,
    title: 'You build real things',
    body: 'Not theory. You join a Build Team and ship something across the summer — collectibles, audio, AI, content, product, or events.'
  }, {
    icon: Sparkles,
    title: 'Use the actual AI tools builders use',
    body: 'Hands-on with Claude Code, Claude Cowork, Nano Banana Pro and the latest agentic AI — the same stack used by startups today.'
  }, {
    icon: Trophy,
    title: 'Mentors who actually built it',
    body: 'Direct access to Emmy and Oscar-winning creators, producers, and faculty from Ivey and INSEAD. People you couldn\u2019t normally get a coffee with.'
  }, {
    icon: UserPlus,
    title: 'Apply with a friend',
    body: 'You can apply solo or with a friend. Some of the best work happens in pairs — and you\u2019ll meet other ambitious students like you.'
  }, {
    icon: Mail,
    title: 'A letter that actually opens doors',
    body: 'A recommendation from Professor Ash Singh based on the work you did — not a generic teacher reference. Universities notice.'
  }, {
    icon: Heart,
    title: 'You become part of something',
    body: 'You contribute to Canada\u2019s most ambitious immersive cultural production. You can point to it the rest of your life.'
  }];
  return <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10" data-id="element-3233">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.07),_transparent_60%)] pointer-events-none" data-id="element-3234" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-3235">
        <div className="max-w-3xl mx-auto text-center mb-14" data-id="element-3236">
          <Eyebrow data-id="element-3237">Why This Matters</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-5 leading-[1.05] tracking-tight" data-id="element-3238">
            For Parents. For <span className="text-amber-400" data-id="element-3239">Students.</span>
          </h2>
          <div className="w-16 h-px bg-amber-400/30 mb-6 mx-auto" data-id="element-3240" />
          <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed" data-id="element-3241">
            Two perspectives. One opportunity. Read what speaks to you.
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3242">
          {/* FOR PARENTS */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative border border-amber-400/30 bg-gradient-to-b from-amber-950/15 via-black/40 to-black p-7 sm:p-8" data-id="element-3243">
            <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3244" />
            <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-amber-400/45 bg-amber-400/10 mb-6" data-id="element-3245">
              <ShieldCheck className="w-3 h-3 text-amber-400" data-id="element-3246" />
              <span className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3247">
                For Parents
              </span>
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight mb-3" data-id="element-3248">
              Why your child should apply
            </h3>
            <p className="text-white/60 text-sm font-light leading-relaxed mb-7" data-id="element-3249">
              A serious, supervised summer opportunity that builds the skills
              and credentials universities are paying attention to right now.
            </p>
            <div className="space-y-4" data-id="element-3250">
              {parentPoints.map(p => <div key={p.title} className="flex items-start gap-3 pb-4 border-b border-amber-400/10 last:border-b-0 last:pb-0" data-id="element-3251">
                  <div className="w-8 h-8 border border-amber-400/35 bg-black/40 flex items-center justify-center shrink-0" data-id="element-3252">
                    <p.icon className="w-3.5 h-3.5 text-amber-400" data-id="element-3253" />
                  </div>
                  <div className="flex-1" data-id="element-3254">
                    <p className="text-white text-sm font-medium leading-snug mb-1" data-id="element-3255">
                      {p.title}
                    </p>
                    <p className="text-white/55 text-xs sm:text-[13px] font-light leading-relaxed" data-id="element-3256">
                      {p.body}
                    </p>
                  </div>
                </div>)}
            </div>
            <a href="#apply" className="mt-7 group inline-flex items-center justify-center gap-2 w-full px-5 py-3.5 border border-amber-400/50 hover:border-amber-400 hover:bg-amber-400/5 text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold transition-colors" data-id="element-3257">
              Apply on Behalf of Your Child
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-3258" />
            </a>
          </motion.div>

          {/* FOR STUDENTS */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="relative border border-amber-400/30 bg-gradient-to-b from-amber-950/15 via-black/40 to-black p-7 sm:p-8" data-id="element-3259">
            <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3260" />
            <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-amber-400/45 bg-amber-400/10 mb-6" data-id="element-3261">
              <Rocket className="w-3 h-3 text-amber-400" data-id="element-3262" />
              <span className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3263">
                For Students
              </span>
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight mb-3" data-id="element-3264">
              Why you should apply
            </h3>
            <p className="text-white/60 text-sm font-light leading-relaxed mb-7" data-id="element-3265">
              This isn&rsquo;t a course. You get to build real things with real
              people who&rsquo;ve done it — and walk away with something
              you&rsquo;re actually proud of.
            </p>
            <div className="space-y-4" data-id="element-3266">
              {studentPoints.map(p => <div key={p.title} className="flex items-start gap-3 pb-4 border-b border-amber-400/10 last:border-b-0 last:pb-0" data-id="element-3267">
                  <div className="w-8 h-8 border border-amber-400/35 bg-black/40 flex items-center justify-center shrink-0" data-id="element-3268">
                    <p.icon className="w-3.5 h-3.5 text-amber-400" data-id="element-3269" />
                  </div>
                  <div className="flex-1" data-id="element-3270">
                    <p className="text-white text-sm font-medium leading-snug mb-1" data-id="element-3271">
                      {p.title}
                    </p>
                    <p className="text-white/55 text-xs sm:text-[13px] font-light leading-relaxed" data-id="element-3272">
                      {p.body}
                    </p>
                  </div>
                </div>)}
            </div>
            <a href="#apply" className="mt-7 group inline-flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-amber-400 text-black text-[10px] tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_30px_rgba(212,175,55,0.3)]" data-id="element-3273">
              Start Your Application
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-3274" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
};
// ====================================== SELECTIVITY (with competitive %)
const SelectivityBlock = () => {
  const items = [{
    icon: Percent,
    title: 'Only a Small Percentage Selected',
    body: 'Due to limited capacity, only a small percentage of applicants are accepted. Apply thoughtfully.'
  }, {
    icon: Award,
    title: 'Limited Cohort',
    body: 'We invest deeply in those we accept. Spots are intentionally few.'
  }, {
    icon: ShieldCheck,
    title: 'Careful Review',
    body: 'Every application is reviewed for fit, commitment, and potential.'
  }];
  return <section className="relative py-20 md:py-24 bg-black border-t border-amber-400/10" data-id="element-3275">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.05),_transparent_70%)] pointer-events-none" data-id="element-3276" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-3277">
        <SectionHeading eyebrow="Competitive Selection" title="High Demand." highlight="Few Spots." intro="This is a serious application with a serious review process. Take it seriously and your effort will show." data-id="element-3278" />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto" data-id="element-3279">
          {items.map((it, i) => <motion.div key={it.title} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} data-id="element-3280">
              <PremiumCard icon={it.icon} title={it.title} body={it.body} highlight data-id="element-3281" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// ====================================== WHAT FELLOWS DO
const WhatYoullWorkOn = () => {
  const areas = [{
    icon: Brain,
    title: 'Artificial Intelligence',
    body: 'Build with generative AI — agents, automations, applied tools that ship.'
  }, {
    icon: Briefcase,
    title: 'Entrepreneurship',
    body: 'Product thinking, business models, customer discovery, real shipping discipline.'
  }, {
    icon: Camera,
    title: 'Digital Media',
    body: 'Storytelling, content production, brand strategy, audience growth.'
  }, {
    icon: Layers,
    title: 'Culture & Heritage',
    body: 'Sikh history, identity, and culture brought to life through modern formats.'
  }, {
    icon: PartyPopper,
    title: 'Live Events',
    body: 'Contribute to large-scale productions, festivals, and immersive activations.'
  }, {
    icon: Sparkles,
    title: 'Technology Stack',
    body: 'Web, mobile, immersive tech, collectibles, and the production pipelines behind them.'
  }];
  return <section className="relative py-24 md:py-32 bg-[#080808] border-t border-amber-400/10" data-id="element-3282">
      <div className="container mx-auto px-4" data-id="element-3283">
        <SectionHeading eyebrow="What You'll Actually Do" title="Six Disciplines." highlight="One Real Project." intro="Fellows pick an AI-related workstream and ship something across the summer. Not lectures — building." data-id="element-3284" />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto" data-id="element-3285">
          {areas.map((a, i) => <motion.div key={a.title} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.05
        }} data-id="element-3286">
              <PremiumCard icon={a.icon} title={a.title} body={a.body} data-id="element-3287" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// ====================================== STUDENT BUILD TEAMS
const BuildTeams = () => {
  const teams = [{
    icon: Coins,
    tag: 'Systems & Mechanics',
    name: 'Token & XP System',
    tagline: 'Design how progression actually works.',
    bullets: ['Design the XP system and progression logic', 'Shape utility, unlocks, and redemption mechanics', 'Explore how tokens / rewards connect to behavior']
  }, {
    icon: Gem,
    tag: 'Physical × Digital',
    name: 'Coinage & Collectibles',
    tagline: 'Bring event coins and collectibles to life.',
    bullets: ['Design event coin concepts and storytelling', 'Shape physical collectible ideas', 'Connect physical and digital collectible logic']
  }, {
    icon: Wine,
    tag: 'Sensory & Story',
    name: 'Mocktails & Sensory',
    tagline: 'Build the taste and memory layer of the world.',
    bullets: ['Research pairings and historical drink references', 'Name and frame mocktails', 'Connect scent, drink, and memory sequences']
  }, {
    icon: Crown,
    tag: 'Digital Product',
    name: 'Lahore Treasury / Digital Collectibles',
    tagline: 'Help build the digital court.',
    bullets: ['Shape cards, sets, utility, and rewards', 'Design Misls, XP, and engagement logic', 'Test onboarding and growth ideas for younger audiences']
  }, {
    icon: Mic,
    tag: 'Audio & Narrative',
    name: 'Audio World / Podcast',
    tagline: 'Help shape the audio franchise.',
    bullets: ['Develop episode ideas, research, and character arcs', 'Support sound and story packaging', 'Think through social distribution for younger audiences']
  }, {
    icon: Monitor,
    tag: 'Web · Schools · Social',
    name: 'Website, School Pitch & Content',
    tagline: 'Make the public surface sharper.',
    bullets: ['Optimize landing pages, messaging, and user journeys', 'Frame school pitches and educator-friendly materials', 'Make short-form content and behind-the-scenes storytelling']
  }];
  return <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10" data-id="element-3288">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.07),_transparent_60%)] pointer-events-none" data-id="element-3289" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-3290">
        <div className="max-w-3xl mx-auto text-center" data-id="element-3291">
          <Eyebrow data-id="element-3292">A Student Venture Studio Around Sikh Kingdoms</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-3293">
            If Selected, You&rsquo;ll Join a{' '}
            <span className="text-amber-400" data-id="element-3294">Build Team</span>
          </h2>
          <div className="w-16 h-px bg-amber-400/30 mb-6 mx-auto" data-id="element-3295" />
          <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-3296">
            Accepted fellows are placed onto a focused student team working
            across AI, culture, storytelling, digital products, events, and
            entrepreneurship. Placement is matched to your strengths and what
            the cohort needs.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto" data-id="element-3297">
          {teams.map((t, i) => <motion.div key={t.name} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.06
        }} className="group relative border border-amber-400/20 bg-gradient-to-b from-amber-950/12 via-black/40 to-black hover:border-amber-400/55 transition-colors p-6 sm:p-7 flex flex-col" data-id="element-3298">
              <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/70 to-transparent" data-id="element-3299" />
              <div className="flex items-start justify-between mb-5 gap-3" data-id="element-3300">
                <div className="w-11 h-11 border border-amber-400/45 bg-black/40 flex items-center justify-center shrink-0" data-id="element-3301">
                  <t.icon className="w-5 h-5 text-amber-400" data-id="element-3302" />
                </div>
                <span className="text-[9px] tracking-[0.25em] uppercase text-amber-400/80 border border-amber-400/30 px-2 py-1 bg-amber-400/5 text-right" data-id="element-3303">
                  {t.tag}
                </span>
              </div>
              <p className="text-amber-400/70 text-[10px] tracking-[0.3em] uppercase font-bold mb-1" data-id="element-3304">
                Build Team
              </p>
              <h3 className="font-serif text-xl text-white leading-tight mb-2" data-id="element-3305">
                {t.name}
              </h3>
              <p className="text-white/75 text-sm font-light italic mb-4" data-id="element-3306">
                {t.tagline}
              </p>
              <ul className="space-y-2 pt-4 border-t border-amber-400/10 mt-auto" data-id="element-3307">
                {t.bullets.map(b => <li key={b} className="flex items-start gap-2.5 text-white/60 text-[13px] font-light leading-relaxed" data-id="element-3308">
                    <ChevronRight className="w-3 h-3 text-amber-400/80 mt-1 shrink-0" data-id="element-3309" />
                    <span data-id="element-3310">{b}</span>
                  </li>)}
              </ul>
            </motion.div>)}
        </div>

        {/* Operating model */}
        <div className="mt-14 max-w-5xl mx-auto" data-id="element-3311">
          <div className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-7 sm:p-9" data-id="element-3312">
            <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3313" />
            <div className="flex items-center gap-2 mb-5 justify-center" data-id="element-3314">
              <Target className="w-3.5 h-3.5 text-amber-400" data-id="element-3315" />
              <span className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3316">
                How It Operates
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-3317">
              {[{
              label: '1 Primary Team',
              sub: 'No floating across everything'
            }, {
              label: '1 Support Mentor',
              sub: 'Dedicated to your team'
            }, {
              label: '1 Specific Deliverable',
              sub: 'Shipped, not theoretical'
            }, {
              label: 'Friday Check-In',
              sub: 'Weekly cohort sync'
            }].map(m => <div key={m.label} className="border border-amber-400/15 bg-black/40 p-4 text-center" data-id="element-3318">
                  <p className="text-white font-serif text-sm sm:text-base leading-tight mb-1" data-id="element-3319">
                    {m.label}
                  </p>
                  <p className="text-white/45 text-[11px] font-light leading-snug" data-id="element-3320">
                    {m.sub}
                  </p>
                </div>)}
            </div>
            <p className="text-center mt-6 text-white/55 text-sm font-light italic" data-id="element-3321">
              Not just a fellowship — a{' '}
              <span className="text-amber-400 not-italic font-medium" data-id="element-3322">
                student venture studio
              </span>{' '}
              around Sikh Kingdoms.
            </p>
          </div>
        </div>
      </div>
    </section>;
};
// ====================================== AI TOOLS
const AITools = () => {
  const tools = [{
    icon: Banana,
    name: 'Nano Banana Pro',
    tag: 'Image & Vision',
    body: 'Cutting-edge image generation and visual workflows used by modern creative teams.'
  }, {
    icon: Code,
    name: 'Claude Code',
    tag: 'Engineering',
    body: 'Pair-program with Anthropic\u2019s coding agent. Build software end-to-end.'
  }, {
    icon: Cpu,
    name: 'Claude Cowork',
    tag: 'Multi-Agent Workflows',
    body: 'Collaborative AI workspaces where teams ship alongside intelligent agents.'
  }];
  return <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10" data-id="element-3323">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.08),_transparent_60%)] pointer-events-none" data-id="element-3324" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-3325">
        <SectionHeading eyebrow="The Stack You'll Use" title="Real AI Tools." highlight="Used by Builders Today." intro="Students will gain exposure to modern AI workflows and tools used by builders, startups, and innovators today." data-id="element-3326" />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto" data-id="element-3327">
          {tools.map((t, i) => <motion.div key={t.name} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className="relative border border-amber-400/30 bg-gradient-to-b from-amber-950/15 via-black to-black p-7 hover:border-amber-400/60 transition-colors" data-id="element-3328">
              <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/80 to-transparent" data-id="element-3329" />
              <div className="flex items-center justify-between mb-5" data-id="element-3330">
                <div className="w-12 h-12 border border-amber-400/50 bg-black/40 flex items-center justify-center" data-id="element-3331">
                  <t.icon className="w-5 h-5 text-amber-400" data-id="element-3332" />
                </div>
                <span className="text-[9px] tracking-[0.25em] uppercase text-amber-400/80 border border-amber-400/30 px-2 py-1 bg-amber-400/5" data-id="element-3333">
                  {t.tag}
                </span>
              </div>
              <h4 className="font-serif text-xl text-white mb-3 leading-tight" data-id="element-3334">
                {t.name}
              </h4>
              <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-3335">
                {t.body}
              </p>
            </motion.div>)}
        </div>
        <p className="text-center text-white/40 text-xs mt-8 italic font-light" data-id="element-3336">
          + ongoing exposure to the latest agentic, generative, and applied AI
          tooling.
        </p>
      </div>
    </section>;
};
// ====================================== MENTORS / ASH SINGH
const ThePeople = () => <section className="relative py-24 md:py-32 bg-[#080808] border-t border-amber-400/10 overflow-hidden" data-id="element-3337">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.07),_transparent_60%)] pointer-events-none" data-id="element-3338" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3339">
      <SectionHeading eyebrow="The People You'll Work With" title="Mentors Who" highlight="Actually Built Things" intro="The people guiding you through the summer aren't theorists. They're operators, creators, and award-winning artists." data-id="element-3340" />
      <div className="max-w-6xl mx-auto mt-14 space-y-5" data-id="element-3341">
        {/* Award winners */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-7 sm:p-9" data-id="element-3342">
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3343" />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-7 items-start" data-id="element-3344">
            <div className="lg:col-span-2" data-id="element-3345">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-amber-400/40 bg-amber-400/10 mb-4" data-id="element-3346">
                <Trophy className="w-3 h-3 text-amber-400" data-id="element-3347" />
                <span className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3348">
                  Award-Winning Creators
                </span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight mb-3" data-id="element-3349">
                Emmy &amp; Oscar-winning mentors
              </h3>
              <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-3350">
                Direct access to the creators, producers, and artists shaping
                the immersive production.
              </p>
            </div>
            <div className="lg:col-span-3 grid grid-cols-2 gap-3" data-id="element-3351">
              {[{
              icon: Trophy,
              label: 'Emmy Winners',
              sub: 'Television & narrative'
            }, {
              icon: Star,
              label: 'Oscar Winners',
              sub: 'Film & creative direction'
            }, {
              icon: Film,
              label: 'Producers',
              sub: 'Live & immersive'
            }, {
              icon: Music,
              label: 'Composers',
              sub: 'Score & sound design'
            }].map(c => <div key={c.label} className="border border-amber-400/20 bg-black/40 p-4 flex items-start gap-3" data-id="element-3352">
                  <c.icon className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" data-id="element-3353" />
                  <div data-id="element-3354">
                    <p className="text-white text-sm font-medium leading-tight" data-id="element-3355">
                      {c.label}
                    </p>
                    <p className="text-white/45 text-[11px] mt-0.5 font-light" data-id="element-3356">
                      {c.sub}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* Ash Singh */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="relative border-2 border-amber-400/50 bg-gradient-to-br from-amber-950/25 via-black to-black p-7 sm:p-9 shadow-[0_0_50px_rgba(212,175,55,0.15)]" data-id="element-3357">
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3358" />
          <span className="absolute -top-3 left-8 text-[9px] tracking-[0.3em] uppercase text-black bg-amber-400 px-2 py-1 font-bold" data-id="element-3359">
            Featured Faculty
          </span>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-7 items-center" data-id="element-3360">
            <div className="lg:col-span-2 flex flex-col items-start" data-id="element-3361">
              <div className="relative w-24 h-24 rounded-full border-2 border-amber-400 bg-gradient-to-br from-amber-400/30 to-amber-950/30 flex items-center justify-center mb-5 shadow-[0_0_30px_rgba(212,175,55,0.3)]" data-id="element-3362">
                <span className="font-serif text-3xl text-amber-400" data-id="element-3363">AS</span>
              </div>
              <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold mb-2" data-id="element-3364">
                Monthly Sessions
              </p>
              <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight mb-2" data-id="element-3365">
                Professor Ash Singh
              </h3>
              <p className="text-white/55 text-sm font-light leading-relaxed mb-4" data-id="element-3366">
                One of the top adjunct professors of entrepreneurship globally.
              </p>
              <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-amber-400/40 bg-amber-400/10" data-id="element-3367">
                <Tv className="w-3 h-3 text-amber-400" data-id="element-3368" />
                <span className="text-amber-400 text-[9px] tracking-[0.25em] uppercase font-bold" data-id="element-3369">
                  Featured on CNA · Millionaire Minds
                </span>
              </div>
            </div>
            <div className="lg:col-span-3 space-y-3" data-id="element-3370">
              <div className="border border-amber-400/25 bg-black/40 p-4" data-id="element-3371">
                <div className="flex items-start gap-3" data-id="element-3372">
                  <GraduationCap className="w-4 h-4 text-amber-400 mt-1 shrink-0" data-id="element-3373" />
                  <div className="flex-1" data-id="element-3374">
                    <div className="flex items-center justify-between gap-2 mb-1" data-id="element-3375">
                      <p className="text-amber-400/80 text-[10px] tracking-[0.25em] uppercase font-bold" data-id="element-3376">
                        Ivey Business School
                      </p>
                      <span className="text-[9px] tracking-[0.2em] uppercase text-amber-400 border border-amber-400/40 px-1.5 py-0.5 bg-amber-400/10" data-id="element-3377">
                        Canada&rsquo;s #1
                      </span>
                    </div>
                    <p className="text-white text-sm font-light leading-snug" data-id="element-3378">
                      Adjunct Professor of Entrepreneurship
                    </p>
                  </div>
                </div>
              </div>
              <div className="border border-amber-400/25 bg-black/40 p-4" data-id="element-3379">
                <div className="flex items-start gap-3" data-id="element-3380">
                  <Globe className="w-4 h-4 text-amber-400 mt-1 shrink-0" data-id="element-3381" />
                  <div className="flex-1" data-id="element-3382">
                    <div className="flex items-center justify-between gap-2 mb-1" data-id="element-3383">
                      <p className="text-amber-400/80 text-[10px] tracking-[0.25em] uppercase font-bold" data-id="element-3384">
                        INSEAD Business School
                      </p>
                      <span className="text-[9px] tracking-[0.2em] uppercase text-amber-400 border border-amber-400/40 px-1.5 py-0.5 bg-amber-400/10" data-id="element-3385">
                        Europe&rsquo;s #1
                      </span>
                    </div>
                    <p className="text-white text-sm font-light leading-snug" data-id="element-3386">
                      Adjunct Professor of Entrepreneurship · France
                    </p>
                  </div>
                </div>
              </div>
              <div className="border border-amber-400/45 bg-amber-400/10 p-4" data-id="element-3387">
                <div className="flex items-start gap-3" data-id="element-3388">
                  <Mail className="w-4 h-4 text-amber-400 mt-1 shrink-0" data-id="element-3389" />
                  <div data-id="element-3390">
                    <p className="text-amber-400 text-[10px] tracking-[0.25em] uppercase font-bold mb-1" data-id="element-3391">
                      What You Earn
                    </p>
                    <p className="text-white text-sm font-light leading-snug" data-id="element-3392">
                      A{' '}
                      <span className="text-amber-400 font-medium" data-id="element-3393">
                        letter of recommendation from Professor Ash Singh
                      </span>{' '}
                      based on the work you actually contribute through the
                      fellowship.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CNA Millionaire Minds video — embedded inside the Ash Singh card */}
          <div className="mt-8 pt-7 border-t border-amber-400/15" data-id="element-3394">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4" data-id="element-3395">
              <div data-id="element-3396">
                <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold mb-1" data-id="element-3397">
                  Featured by Channel News Asia
                </p>
                <p className="text-white/65 text-sm font-light italic" data-id="element-3398">
                  Professor Ash Singh on{' '}
                  <span className="text-amber-400 not-italic" data-id="element-3399">
                    Millionaire Minds
                  </span>
                </p>
              </div>
              <span className="text-white/35 text-[10px] tracking-wide font-light italic" data-id="element-3400">
                Global broadcast feature
              </span>
            </div>
            <div className="relative aspect-video bg-black overflow-hidden border border-amber-400/25" data-id="element-3401">
              <iframe className="absolute inset-0 w-full h-full" src="https://www.youtube.com/embed/2c0eBgOG2yU?rel=0" title="Professor Ash Singh — Channel News Asia: Millionaire Minds" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-3402" />
            </div>
          </div>
        </motion.div>

        {/* MBA pairing */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.15
      }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-7 sm:p-9" data-id="element-3403">
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3404" />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-7 items-start" data-id="element-3405">
            <div className="lg:col-span-2" data-id="element-3406">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-amber-400/40 bg-amber-400/10 mb-4" data-id="element-3407">
                <UserCircle className="w-3 h-3 text-amber-400" data-id="element-3408" />
                <span className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3409">
                  1:1 Pairing
                </span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight mb-3" data-id="element-3410">
                Paired with an Ivey MBA
              </h3>
              <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-3411">
                Each fellow is individually matched with a current MBA student
                from the Ivey Business School as a personal mentor.
              </p>
            </div>
            <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3412">
              {['One-on-one weekly support', 'Career and project guidance', 'Network into Ivey alumni', 'Real conversations, not formal mentoring'].map(b => <div key={b} className="border border-amber-400/15 bg-black/40 p-4 flex items-start gap-3" data-id="element-3413">
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" data-id="element-3414" />
                  <span className="text-white/75 text-sm font-light leading-snug" data-id="element-3415">
                    {b}
                  </span>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>;
// ====================================== LOCATION
const Location = () => <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10" data-id="element-3416">
    <div className="container mx-auto px-4" data-id="element-3417">
      <SectionHeading eyebrow="Where It Happens" title="In Person." highlight="Or Virtual." intro="Selected students may participate in person at the Illuminarium in downtown Toronto&rsquo;s Distillery District — or virtually from anywhere — depending on availability, location, and cohort structure." data-id="element-3418" />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto" data-id="element-3419">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-7" data-id="element-3420">
          <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3421" />
          <div className="flex items-center gap-3 mb-4" data-id="element-3422">
            <div className="w-11 h-11 border border-amber-400/50 bg-black/40 flex items-center justify-center" data-id="element-3423">
              <MapPin className="w-5 h-5 text-amber-400" data-id="element-3424" />
            </div>
            <span className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3425">
              In Person
            </span>
          </div>
          <h3 className="font-serif text-2xl text-white leading-tight mb-2" data-id="element-3426">
            At the Illuminarium
          </h3>
          <p className="text-amber-400/85 text-[11px] tracking-[0.25em] uppercase font-bold mb-3" data-id="element-3427">
            Downtown Toronto · Distillery District
          </p>
          <p className="text-white/60 text-sm font-light leading-relaxed" data-id="element-3428">
            Selected students may participate on-site at the Illuminarium in
            Toronto&rsquo;s historic Distillery District — surrounded by the
            production, the creators, and the live cultural work.
          </p>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-7" data-id="element-3429">
          <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3430" />
          <div className="flex items-center gap-3 mb-4" data-id="element-3431">
            <div className="w-11 h-11 border border-amber-400/50 bg-black/40 flex items-center justify-center" data-id="element-3432">
              <Wifi className="w-5 h-5 text-amber-400" data-id="element-3433" />
            </div>
            <span className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3434">
              Virtual
            </span>
          </div>
          <h3 className="font-serif text-2xl text-white leading-tight mb-3" data-id="element-3435">
            Anywhere in the World
          </h3>
          <p className="text-white/60 text-sm font-light leading-relaxed" data-id="element-3436">
            Selected students outside the region or with scheduling needs may
            participate virtually with the same full access to mentors, tools,
            and project work.
          </p>
        </motion.div>
      </div>
      <p className="text-center mt-8 text-white/40 text-xs font-light italic max-w-2xl mx-auto" data-id="element-3437">
        Format assignment is based on availability, location, and cohort
        structure.
      </p>
    </div>
  </section>;
// ====================================== COMMUNITY INITIATIVES
const CommunityInitiatives = () => {
  const items = [{
    icon: HandHeart,
    title: 'Community-Driven Innovation',
    body: 'Fellows contribute to projects with real impact in their communities.'
  }, {
    icon: Users,
    title: 'Real-World Collaboration',
    body: 'Work alongside other fellows, mentors, and partners on shared initiatives.'
  }, {
    icon: Rocket,
    title: 'Leadership Opportunities',
    body: 'Lead a workstream, run a session, or own a real piece of the production.'
  }, {
    icon: Sparkles,
    title: 'Meaningful Projects',
    body: 'Beyond workshops — fellows ship things that matter to people outside the cohort.'
  }];
  return <section className="relative py-24 md:py-32 bg-[#080808] border-t border-amber-400/10" data-id="element-3438">
      <div className="container mx-auto px-4" data-id="element-3439">
        <SectionHeading eyebrow="Beyond the Sessions" title="Real Impact." highlight="Real Collaboration." intro="Beyond workshops and sessions, fellows participate in community initiatives, collaborative projects, and real-world AI innovation experiences." data-id="element-3440" />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto" data-id="element-3441">
          {items.map((it, i) => <motion.div key={it.title} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.06
        }} data-id="element-3442">
              <PremiumCard icon={it.icon} title={it.title} body={it.body} data-id="element-3443" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// ====================================== APPLY WITH A FRIEND
const ApplyWithFriend = () => <section className="relative py-20 md:py-24 bg-black border-t border-amber-400/10" data-id="element-3444">
    <div className="container mx-auto px-4" data-id="element-3445">
      <div className="max-w-5xl mx-auto" data-id="element-3446">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-8 sm:p-10 lg:p-12 text-center" data-id="element-3447">
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3448" />
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/40 bg-amber-400/10 mb-6" data-id="element-3449">
            <UserPlus className="w-3.5 h-3.5 text-amber-400" data-id="element-3450" />
            <span className="text-amber-400 tracking-[0.3em] text-[10px] uppercase font-bold" data-id="element-3451">
              Apply With a Friend
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-4 leading-tight" data-id="element-3452">
            The best work happens{' '}
            <span className="text-amber-400 italic" data-id="element-3453">in pairs.</span>
          </h2>
          <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed max-w-2xl mx-auto mb-6" data-id="element-3454">
            Students are encouraged to apply with a friend or partner. Some
            activities and projects are collaborative — teamwork, creativity,
            and shared learning are part of the design.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-2xl mx-auto" data-id="element-3455">
            {['Work in pairs', 'Learn together', 'Build accountability', 'Ship as a team'].map(b => <div key={b} className="border border-amber-400/20 bg-black/40 px-3 py-2 text-[11px] tracking-wide text-white/70 font-light" data-id="element-3456">
                {b}
              </div>)}
          </div>
        </motion.div>
      </div>
    </div>
  </section>;
// ====================================== COST & COMMITMENT (FREE + REFUNDABLE DEPOSIT)
const CostCommitment = () => <section className="relative py-24 md:py-32 bg-[#080808] border-t border-amber-400/10" data-id="element-3457">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.06),_transparent_60%)] pointer-events-none" data-id="element-3458" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3459">
      <div className="max-w-5xl mx-auto" data-id="element-3460">
        <div className="text-center mb-12" data-id="element-3461">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400 bg-amber-400/10 mb-6 shadow-[0_0_25px_rgba(212,175,55,0.15)]" data-id="element-3462">
            <Gift className="w-3.5 h-3.5 text-amber-400" data-id="element-3463" />
            <span className="text-amber-400 tracking-[0.3em] text-[10px] uppercase font-bold" data-id="element-3464">
              Fully Sponsored for Selected Students
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mb-5 leading-[1.05] tracking-tight" data-id="element-3465">
            The Opportunity is <span className="text-amber-400" data-id="element-3466">Free.</span>
          </h2>
          <div className="w-16 h-px bg-amber-400/40 mx-auto mb-6" data-id="element-3467" />
          <p className="text-white/70 text-base sm:text-lg font-light leading-relaxed max-w-3xl mx-auto" data-id="element-3468">
            The fellowship itself is fully sponsored for selected students. To
            ensure commitment and accountability, accepted students may be
            required to place a refundable{' '}
            <span className="text-amber-400 font-medium" data-id="element-3469">USD $250 deposit</span>
            , returned in full upon successful completion of the program.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-id="element-3470">
          {/* Program is Free */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="relative border-2 border-amber-400/50 bg-gradient-to-br from-amber-950/20 via-black to-black p-8 shadow-[0_0_40px_rgba(212,175,55,0.15)]" data-id="element-3471">
            <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3472" />
            <div className="flex items-center gap-3 mb-5" data-id="element-3473">
              <div className="w-12 h-12 border border-amber-400 bg-amber-400/10 flex items-center justify-center" data-id="element-3474">
                <Gift className="w-5 h-5 text-amber-400" data-id="element-3475" />
              </div>
              <div data-id="element-3476">
                <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3477">
                  Program Cost
                </p>
                <p className="font-serif text-3xl sm:text-4xl text-amber-400 leading-none mt-1" data-id="element-3478">
                  Free
                </p>
              </div>
            </div>
            <p className="text-white/70 text-sm font-light leading-relaxed mb-4" data-id="element-3479">
              Fully sponsored for selected students. No tuition, no hidden fees,
              no obligations beyond commitment.
            </p>
            <ul className="space-y-2 pt-4 border-t border-amber-400/15" data-id="element-3480">
              {['Mentorship from Emmy & Oscar winners', 'Faculty access (Ivey & INSEAD)', 'AI tooling and project work', 'Letter of recommendation from Prof. Ash Singh'].map(b => <li key={b} className="flex items-start gap-2.5 text-white/65 text-sm font-light" data-id="element-3481">
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" data-id="element-3482" />
                  <span data-id="element-3483">{b}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* Refundable Deposit */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/12 via-black to-black p-8" data-id="element-3484">
            <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400/70 to-transparent" data-id="element-3485" />
            <div className="flex items-center gap-3 mb-5" data-id="element-3486">
              <div className="w-12 h-12 border border-amber-400/50 bg-black/40 flex items-center justify-center" data-id="element-3487">
                <RefreshCw className="w-5 h-5 text-amber-400" data-id="element-3488" />
              </div>
              <div data-id="element-3489">
                <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-3490">
                  Refundable Commitment
                </p>
                <p className="font-serif text-3xl sm:text-4xl text-white leading-none mt-1" data-id="element-3491">
                  <span className="text-amber-400/80 text-2xl align-top" data-id="element-3492">
                    USD
                  </span>{' '}
                  $250
                </p>
              </div>
            </div>
            <p className="text-white/70 text-sm font-light leading-relaxed mb-4" data-id="element-3493">
              Accepted students may be required to place a refundable deposit.{' '}
              <span className="text-amber-400" data-id="element-3494">Returned in full</span> upon
              successful completion.
            </p>
            <ul className="space-y-2 pt-4 border-t border-amber-400/15" data-id="element-3495">
              {['Ensures commitment and accountability', 'Fully refunded on successful completion', 'Requires attending all required sessions', 'Requires active participation'].map(b => <li key={b} className="flex items-start gap-2.5 text-white/65 text-sm font-light" data-id="element-3496">
                  <DollarSign className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" data-id="element-3497" />
                  <span data-id="element-3498">{b}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>

        {/* Why */}
        <div className="mt-8 border border-amber-400/20 bg-black/40 p-5 sm:p-6" data-id="element-3499">
          <div className="flex items-start gap-4" data-id="element-3500">
            <Info className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" data-id="element-3501" />
            <div data-id="element-3502">
              <p className="text-amber-400 tracking-[0.25em] text-[10px] uppercase font-bold mb-2" data-id="element-3503">
                Why the Deposit?
              </p>
              <p className="text-white/65 text-sm font-light leading-relaxed" data-id="element-3504">
                The deposit exists to ensure that selected students take the
                opportunity seriously and show up fully. Students who complete
                the program receive the full $250 back. The fellowship itself
                remains free.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
// ====================================== APPLY (Typeform)
const Apply = () => <section id="apply" className="relative py-24 md:py-32 bg-black border-t border-amber-400/10 scroll-mt-20" data-id="element-3505">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08),_transparent_70%)] pointer-events-none" data-id="element-3506" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3507">
      <SectionHeading eyebrow="Apply for the Fellowship" title="Begin Your" highlight="Application" intro="Only a small percentage of applicants are accepted. Please take the application seriously — thoughtful responses matter." data-id="element-3508" />

      <motion.div initial={{
      opacity: 0,
      y: 16
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="max-w-3xl mx-auto mt-12 mb-8 border border-amber-400/25 bg-amber-950/8 p-5 sm:p-6" data-id="element-3509">
        <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold mb-3" data-id="element-3510">
          What We&rsquo;ll Ask
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5 text-sm font-light text-white/70" data-id="element-3511">
          {['Your contact details', 'A bit about yourself', 'Resume or LinkedIn profile', 'Why you want to join', 'Expectations & commitment', 'Whether you\u2019re applying with a friend'].map(q => <div key={q} className="flex items-center gap-2.5" data-id="element-3512">
              <ChevronRight className="w-3 h-3 text-amber-400 shrink-0" data-id="element-3513" />
              <span data-id="element-3514">{q}</span>
            </div>)}
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.8
    }} className="max-w-4xl mx-auto relative border border-amber-400/30 bg-black p-1.5 shadow-[0_0_60px_rgba(212,175,55,0.15)]" data-id="element-3515">
        <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3516" />
        <iframe src="https://form.typeform.com/to/bynEjrbu" title="Entrepreneurship and AI Fellowship Application" className="w-full h-[720px] bg-white" frameBorder="0" allow="camera; microphone; autoplay; encrypted-media; fullscreen" data-id="element-3517" />
      </motion.div>

      <p className="text-center mt-6 text-white/40 text-xs font-light italic flex items-center justify-center gap-2" data-id="element-3518">
        <Heart className="w-3 h-3 text-amber-400" data-id="element-3519" />
        Applications reviewed on a rolling basis · Limited spots
      </p>
    </div>
  </section>;
// ====================================== FINAL CTA
const FinalCTA = () => <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10 overflow-hidden" data-id="element-3520">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.1),_transparent_60%)] pointer-events-none" data-id="element-3521" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3522">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="max-w-3xl mx-auto text-center" data-id="element-3523">
        <Eyebrow data-id="element-3524">For Students &amp; Parents</Eyebrow>
        <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-3525">
          A summer that{' '}
          <span className="text-amber-400 italic" data-id="element-3526">actually matters.</span>
        </h2>
        <div className="w-20 h-px bg-amber-400/40 mx-auto mb-8" data-id="element-3527" />
        <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed mb-4" data-id="element-3528">
          For ambitious high school and university students ready to build with
          AI, learn from world-class faculty, and earn something real.
        </p>
        <p className="text-sm text-white/45 font-light leading-relaxed mb-10" data-id="element-3529">
          Just 3 hours a week. Free for selected students. Refundable $250
          commitment. Limited spots.
        </p>
        <a href="#apply" className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-amber-400 text-black text-xs tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_50px_rgba(212,175,55,0.4)]" data-id="element-3530">
          Begin the Application
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" data-id="element-3531" />
        </a>
        <p className="mt-6 text-white/40 text-xs font-light italic" data-id="element-3532">
          Parents are welcome to submit on behalf of their child.
        </p>
        <p className="mt-3 text-amber-400/70 text-xs font-light tracking-[0.25em] uppercase" data-id="element-3533">
          Presented by Akal Steel
        </p>
        <p className="mt-1 text-white/35 text-[11px] font-light tracking-wide" data-id="element-3534">
          In partnership with the Sikh Foundation of Canada
        </p>
      </motion.div>
    </div>
  </section>;
// ====================================== PAGE
export const AIEntrepreneurshipPage = () => <LanguageProvider data-id="element-3535">
    <div className="bg-black min-h-screen text-white selection:bg-amber-400/30" data-id="element-3536">
      <ScrollProgress data-id="element-3537" />
      <Navbar data-id="element-3538" />
      <main data-id="element-3539">
        <TrailerIntro data-id="element-3540" />
        <Hero data-id="element-3541" />
        <AtAGlance data-id="element-3542" />
        <WhyThisMatters data-id="element-3543" />
        <ThePeople data-id="element-3544" />
        <SelectivityBlock data-id="element-3545" />
        <WhatYoullWorkOn data-id="element-3546" />
        <BuildTeams data-id="element-3547" />
        <AITools data-id="element-3548" />
        <Location data-id="element-3549" />
        <CommunityInitiatives data-id="element-3550" />
        <ApplyWithFriend data-id="element-3551" />
        <CostCommitment data-id="element-3552" />
        <Apply data-id="element-3553" />
        <FinalCTA data-id="element-3554" />
      </main>
      <LandingFooter data-id="element-3555" />
    </div>
  </LanguageProvider>;
// ====================================== LANDING FOOTER (minimal — no nav distractions)
const LandingFooter = () => <footer className="relative bg-black border-t border-amber-400/20 py-12" data-id="element-3556">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(212,175,55,0.06),_transparent_70%)] pointer-events-none" data-id="element-3557" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3558">
      <div className="max-w-3xl mx-auto text-center" data-id="element-3559">
        <p className="font-serif text-amber-400 text-base sm:text-lg tracking-[0.3em] uppercase mb-3" data-id="element-3560">
          Sikh Kingdoms
        </p>
        <p className="text-white/45 text-xs tracking-[0.25em] uppercase mb-6 font-light" data-id="element-3561">
          Entrepreneurship and AI Opportunity · Summer Fellowship
        </p>
        <div className="w-12 h-px bg-amber-400/30 mx-auto mb-6" data-id="element-3562" />
        <p className="text-white/55 text-xs sm:text-sm font-light leading-relaxed mb-6 max-w-xl mx-auto" data-id="element-3563">
          Application only. Free for selected students. Refundable $250
          commitment. Just 3 hours a week.
        </p>
        <a href="#apply" className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-amber-400/45 hover:border-amber-400 hover:bg-amber-400/5 text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold transition-colors mb-8" data-id="element-3564">
          Begin Application
          <ArrowRight className="w-3 h-3" data-id="element-3565" />
        </a>
        <p className="text-amber-400/70 text-[10px] tracking-[0.3em] uppercase font-bold mb-1" data-id="element-3566">
          Presented by Akal Steel
        </p>
        <p className="text-white/30 text-[11px] font-light tracking-wide mb-1" data-id="element-3567">
          In partnership with the Sikh Foundation of Canada
        </p>
        <p className="text-white/25 text-[10px] font-light italic" data-id="element-3568">
          For questions, please apply and we&rsquo;ll be in touch.
        </p>
      </div>
    </div>
  </footer>;